import { useFirebase } from "../contexts/firebase-context";
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from "react";
import {
    Alert,
    AppBar,
    Avatar,
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Snackbar,
    Stack,
    Toolbar,
    Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import routeMeta from "../utils/routeMeta";
import { useThemeMode } from "../contexts/theme-mode-context";
import MuiAlert from "../utils/muiAlerts";
import { subscribeToPushNotifications } from "../utils/pushNotifications";

const Navbar = () => {
    const { signInWithGoogleAccountPopup, signOutFromApp, currentUserInfo, isLoggedIn, isLoading } = useFirebase()
    const { mode, toggleMode } = useThemeMode()
    const location = useLocation()
    const theme = useTheme()
    const [menuOpen, setMenuOpen] = useState(false)
    const [swRegistration, setSwRegistration] = useState(null)
    const [showUpdatePrompt, setShowUpdatePrompt] = useState(false)
    const [pushEnabled, setPushEnabled] = useState(false)
    const isRefreshingRef = useRef(false)

    useEffect(() => {
        const checkPushStatus = async () => {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                return
            }

            const registration = await navigator.serviceWorker.ready
            const existing = await registration.pushManager.getSubscription()
            setPushEnabled(Boolean(existing))
        }

        checkPushStatus().catch(() => undefined)
    }, [])

    useEffect(() => {
        const handleSwUpdate = (event) => {
            if (event.detail?.registration) {
                setSwRegistration(event.detail.registration)
                setShowUpdatePrompt(true)
            }
        }

        window.addEventListener('happyears-sw-update', handleSwUpdate)

        return () => {
            window.removeEventListener('happyears-sw-update', handleSwUpdate)
        }
    }, [])

    useEffect(() => {
        if (!('serviceWorker' in navigator)) {
            return undefined
        }

        const handleControllerChange = () => {
            if (isRefreshingRef.current) {
                return
            }

            isRefreshingRef.current = true
            window.location.reload()
        }

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
        return () => {
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
        }
    }, [])

    const currentThemeLabel = mode === 'dark' ? 'Dark theme' : 'Light theme'

    const handleUpdateClick = () => {
        if (swRegistration?.waiting) {
            swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
            setShowUpdatePrompt(false)
            return
        }

        window.location.reload()
    }

    const handleEnableNotifications = async () => {
        if (!currentUserInfo) {
            MuiAlert.fire('Sign in required', 'Please sign in before enabling notifications.', 'info')
            return
        }

        try {
            await subscribeToPushNotifications(currentUserInfo)
            setPushEnabled(true)
            MuiAlert.fire('Enabled', 'Push notifications enabled successfully.', 'success')
        } catch (error) {
            MuiAlert.fire('Notification setup failed', error.message || 'Unable to enable notifications.', 'error')
        }
    }

    const visibleRoutes = routeMeta.filter((route) => {
        if (!isLoggedIn) {
            return route.path === '/'
        }
        if (!route.accessKey) {
            return true
        }
        return currentUserInfo && true
    })

    const isRouteActive = (routePath) => {
        if (routePath === '/') {
            return location.pathname === '/'
        }
        return location.pathname === routePath || location.pathname.startsWith(`${routePath}/`)
    }

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: alpha(theme.palette.background.paper, mode === 'dark' ? 0.84 : 0.9),
                    color: 'text.primary',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Toolbar sx={{ gap: 1.5, minHeight: 74 }}>
                    <Link to="/" style={{ textDecoration: 'none' }}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                            <Box
                                sx={{
                                    bgcolor: 'common.white',
                                    borderRadius: 2,
                                    px: 1.1,
                                    py: 0.35,
                                    border: `1px solid ${theme.palette.divider}`
                                }}
                            >
                                <img id='logo' src="/happy_ears_logo.png" alt="company_logo" width="96" />
                            </Box>
                            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700, display: { xs: 'none', md: 'block' } }}>
                                Happy Ears
                            </Typography>
                        </Stack>
                    </Link>

                    <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' }, ml: 2, flexGrow: 1, flexWrap: 'wrap' }}>
                        {
                            visibleRoutes.map((route) => {
                                const RouteIcon = route.Icon
                                const active = isRouteActive(route.path)
                                return (
                                    <Chip
                                        key={route.path}
                                        component={Link}
                                        to={route.path}
                                        clickable
                                        icon={<RouteIcon style={{ fontSize: 16 }} />}
                                        label={route.label}
                                        size="small"
                                        sx={{
                                            bgcolor: active
                                                ? alpha(theme.palette.primary.main, mode === 'dark' ? 0.36 : 0.22)
                                                : mode === 'dark' ? alpha(theme.palette.primary.main, 0.22) : alpha(theme.palette.primary.main, 0.12),
                                            color: 'text.primary',
                                            fontWeight: 700,
                                            fontSize: '0.78rem',
                                            border: `1px solid ${alpha(theme.palette.primary.main, active ? 0.62 : mode === 'dark' ? 0.45 : 0.28)}`,
                                            backdropFilter: 'blur(4px)',
                                            '& .MuiChip-icon': { color: active ? theme.palette.primary.dark : theme.palette.primary.main },
                                            '&:hover': {
                                                bgcolor: mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.18),
                                                transform: 'translateY(-1px)',
                                                boxShadow: theme.shadows[2]
                                            },
                                            boxShadow: active ? theme.shadows[2] : 'none',
                                            transition: 'all 0.18s'
                                        }}
                                    />
                                )
                            })
                        }
                    </Stack>

                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        {
                            isLoading ?
                                <Typography variant="body2">Loading...</Typography>
                                :
                                isLoggedIn ?
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600 }}>{currentUserInfo.displayName}</Typography>
                                        <Button variant="outlined" color="error" size="small" startIcon={<LogoutRoundedIcon />} onClick={() => { signOutFromApp() }} sx={{ minWidth: { xs: 40, sm: 'auto' }, px: { xs: 1, sm: 1.5 } }}>
                                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Logout</Box>
                                        </Button>
                                        <Avatar src={currentUserInfo.photoURL} alt='user_image' referrerPolicy="no-referrer" sx={{ width: 36, height: 36 }} />
                                    </Stack>
                                    :
                                    <Button
                                            onClick={() => { signInWithGoogleAccountPopup() }}
                                            startIcon={
                                                <svg width="18" height="18" viewBox="0 0 48 48">
                                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                                                </svg>
                                            }
                                            sx={{
                                                background: theme.palette.background.paper,
                                                color: 'text.primary',
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 50,
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                                px: 2,
                                                py: 0.7,
                                                textTransform: 'none',
                                                boxShadow: theme.shadows[1],
                                                '&:hover': {
                                                    background: mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.03),
                                                    boxShadow: theme.shadows[3],
                                                    borderColor: theme.palette.primary.main
                                                }
                                            }}
                                        >
                                            Sign in with Google
                                        </Button>
                        }

                        {isLoggedIn && (
                            <IconButton
                                sx={{ color: pushEnabled ? 'success.main' : 'text.primary' }}
                                onClick={handleEnableNotifications}
                                title={pushEnabled ? 'Push notifications enabled' : 'Enable push notifications'}
                            >
                                <NotificationsRoundedIcon />
                            </IconButton>
                        )}

                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{
                                px: 0.5,
                                py: 0.25,
                                borderRadius: 99,
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: alpha(theme.palette.background.paper, mode === 'dark' ? 0.78 : 0.92)
                            }}
                        >
                            <Typography variant="caption" sx={{ px: 0.75, fontWeight: 700, display: { xs: 'none', sm: 'block' } }}>
                                {currentThemeLabel}
                            </Typography>
                            <IconButton
                                sx={{ color: 'text.primary' }}
                                onClick={toggleMode}
                                title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                aria-label={`Current theme: ${currentThemeLabel}. ${mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}`}
                            >
                                {mode === 'dark' ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
                            </IconButton>
                        </Stack>

                        <IconButton sx={{ color: 'text.primary', display: { xs: 'inline-flex', md: 'none' } }} onClick={() => setMenuOpen(true)}>
                            <MenuRoundedIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            <Drawer anchor="right" open={menuOpen} onClose={() => setMenuOpen(false)}>
                <Box sx={{ width: 290, p: 1.5 }}>
                    <Typography variant="h6" sx={{ px: 1, py: 1 }}>Navigation</Typography>
                    <List>
                        {
                            visibleRoutes.map((route) => {
                                const RouteIcon = route.Icon
                                return (
                                    <ListItemButton
                                        key={route.path}
                                        component={Link}
                                        to={route.path}
                                        selected={isRouteActive(route.path)}
                                        onClick={() => setMenuOpen(false)}
                                        sx={{
                                            borderRadius: 1.5,
                                            mb: 0.5,
                                            '&.Mui-selected': {
                                                backgroundColor: alpha(theme.palette.primary.main, mode === 'dark' ? 0.26 : 0.12),
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                            },
                                            '&.Mui-selected:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, mode === 'dark' ? 0.34 : 0.18)
                                            }
                                        }}
                                    >
                                        <ListItemIcon>
                                            <RouteIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary={route.label} />
                                    </ListItemButton>
                                )
                            })
                        }
                    </List>
                </Box>
            </Drawer>

            <Snackbar
                open={showUpdatePrompt}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={() => setShowUpdatePrompt(false)}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    sx={{ width: '100%', alignItems: 'center' }}
                    action={
                        <Button color="inherit" size="small" onClick={handleUpdateClick}>
                            Refresh
                        </Button>
                    }
                >
                    New version available
                </Alert>
            </Snackbar>
        </>
    )
}

export default Navbar
